/**
 * @fileoverview テキストハイライト機能
 * @description 拡張クエリのキーワードでテキストをハイライトする
 * @module apps/web/lib/highlight
 * @remarks
 * ベクトル検索のマッチ位置ではなく、クエリ拡張で生成されたキーワードを使用して
 * テキスト内の関連箇所をハイライト表示する。
 */

/**
 * ハイライトされたテキストセグメント
 */
export interface HighlightSegment {
  /** テキスト内容 */
  text: string
  /** ハイライト対象かどうか */
  isHighlighted: boolean
}

/**
 * テキストをキーワードでハイライト分割する
 * @param text - 対象テキスト
 * @param keywords - ハイライトするキーワード配列
 * @returns ハイライトセグメントの配列
 * @remarks
 * - 大文字小文字を区別しない
 * - 部分一致でハイライト（例: "飛行" で "飛行禁止" もハイライト）
 * - 複数キーワードがオーバーラップする場合は最初のマッチを優先
 */
export function highlightText(text: string, keywords: string[]): HighlightSegment[] {
  if (!keywords || keywords.length === 0) {
    return [{ text, isHighlighted: false }]
  }

  // 空のキーワードを除外し、長いキーワードを優先（部分一致対策）
  const validKeywords = keywords
    .filter((k) => k && k.trim().length > 0)
    .sort((a, b) => b.length - a.length)

  if (validKeywords.length === 0) {
    return [{ text, isHighlighted: false }]
  }

  // 正規表現用にキーワードをエスケープ
  const escapedKeywords = validKeywords.map((k) => escapeRegExp(k))

  // キーワードにマッチする正規表現を作成（大文字小文字無視）
  const pattern = new RegExp(`(${escapedKeywords.join('|')})`, 'gi')

  const segments: HighlightSegment[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = pattern.exec(text)) !== null) {
    // マッチ前のテキストを追加
    if (match.index > lastIndex) {
      segments.push({
        text: text.slice(lastIndex, match.index),
        isHighlighted: false,
      })
    }

    // マッチしたテキストをハイライト
    segments.push({
      text: match[0],
      isHighlighted: true,
    })

    lastIndex = pattern.lastIndex
  }

  // 残りのテキストを追加
  if (lastIndex < text.length) {
    segments.push({
      text: text.slice(lastIndex),
      isHighlighted: false,
    })
  }

  // セグメントが空の場合は元のテキストを返す
  if (segments.length === 0) {
    return [{ text, isHighlighted: false }]
  }

  return segments
}

/**
 * 正規表現の特殊文字をエスケープ
 * @param str - エスケープする文字列
 * @returns エスケープされた文字列
 */
function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * テキストを省略表示する
 * @param text - 対象テキスト
 * @param maxLength - 最大文字数（デフォルト: 100）
 * @param suffix - 省略時の接尾辞（デフォルト: "..."）
 * @returns 省略されたテキスト
 */
export function truncateText(text: string, maxLength = 100, suffix = '...'): string {
  if (text.length <= maxLength) {
    return text
  }
  return text.slice(0, maxLength) + suffix
}
